<template>
    <DCard padding>
        <div class="search">
            <DRow>
                <DCol size="sm-2">
                    <DInput
                        id="search"
                        v-model="filter.search"
                        :label="__('tasks.table.filter.search')"
                        @keydown.enter.exact.prevent="changePage(1)"
                    />
                </DCol>
                <DCol size="sm-2">
                    <DMultiSelect
                        id="status-dropdown"
                        :label="__('tasks.table.filter.status')"
                        :options="statusOptions"
                        selection-type="text"
                        @update="(values) => onFilterUpdate('statuses', values, true)"
                    />
                </DCol>
                <DCol size="sm-2">
                    <DMultiSelect
                        id="type-dropdown"
                        :label="__('tasks.table.filter.type')"
                        :options="typeOptions"
                        selection-type="text"
                        @update="(values) => onFilterUpdate('types', values, true)"
                    />
                </DCol>
                <DCol size="sm-2">
                    <DMultiSelect
                        id="brand-dropdown"
                        :label="__('tasks.table.filter.brand')"
                        :options="brandOptions"
                        selection-type="text"
                        @update="(values) => onFilterUpdate('brands', values, true)"
                    />
                </DCol>
                <DCol size="sm-2">
                    <DMultiSelect
                        id="customer-segments-dropdown"
                        :label="__('tasks.table.filter.customer_segment')"
                        :options="customerSegmentsOptions"
                        selection-type="text"
                        @update="(values) => onFilterUpdate('customer_segments', values, true)"
                    />
                </DCol>
            </DRow>
            <DRow class="mt-2">
                <DCol size="sm-2">
                    <DButton
                        id="filter-apply"
                        :label="__('messages.actions.filter')"
                        @click="changePage(1)"
                    />
                    <DButton
                        id="filter-reset"
                        :label="__('messages.actions.reset_filter')"
                        @click="resetFilters"
                    />
                </DCol>
            </DRow>
            <DRow>
                <DCol>
                    <DButton
                        v-for="(item, key) in groupTypes"
                        v-show="groupItemTypeCount(item)"
                        :id="item.name"
                        :key="key"
                        class="me-1 my-2"
                        :label="__('tasks.table.type_button.' + item.name) + ' (' + groupItemTypeCount(item) + ')'"
                        @click="filterTasksBy(item.types)"
                    />
                </DCol>
            </DRow>
        </div>
        <DRow>
            <DCol>
                <DTable
                    :fields="fields"
                    class="tableText tasks"
                    :items="tasks.data"
                >
                    <template #message="{item}">
                        <td>
                            {{ $page.props.auth.user.language_code === 'de' ? item.message_de : item.message_en }}
                        </td>
                    </template>
                    <template #type="{item}">
                        <td>
                            {{ __('tasks.subject.' + item.type) }}
                        </td>
                    </template>
                    <template #status="{item}">
                        <td>
                            <span :class="[taskOpen(item) ? 'bg-danger' : 'bg-success', 'badge']">
                                {{ __('tasks.table.status_' + taskStatus(item)) }}
                            </span>
                            <DIcon
                                v-if="isBatch(item)"
                                name="content-save-alert"
                            />
                        </td>
                    </template>
                    <template #brand="{item}">
                        <td>
                            {{ item.customer && item.customer.brand ? item.customer.brand.name : '' }}
                        </td>
                    </template>
                    <template #customer="{item}">
                        <td v-if="item.customer_id">
                            <Link
                                v-if="userPermissions.includes('customer.view')"
                                :href="customerUrl(item.customer_id)"
                            >
                                {{ customerName(item.customer) }} ({{ item.customer_id }})
                            </Link>
                            <span
                                v-else
                                class="text-capitalize"
                            >{{ subjectText(item) }}</span>
                        </td>
                        <td v-else>
                            {{ __('tasks.table.no-customer') }}
                        </td>
                    </template>
                    <template #subject="{item}">
                        <td v-if="subjectLinkable(item)">
                            <Link
                                :href="subjectLink(item)"
                            >
                                <span class="text-capitalize">{{ subjectText(item) }}</span>
                            </Link>
                        </td>
                        <td v-else>
                            <span class="text-capitalize">{{ subjectText(item) }}</span>
                        </td>
                    </template>
                    <template #created_at="{item}">
                        <td>
                            {{ setDateFormat(item.created_at) }}
                        </td>
                    </template>
                    <template #updated_at="{item}">
                        <td>
                            {{ setDateFormat(item.updated_at) }}
                        </td>
                    </template>
                    <template #note="{item}">
                        <td
                            v-if="item.note"
                            class="text-break"
                        >
                            <span
                                v-if="item.note.length > 50"
                            >
                                {{ item.note.substring(0, 50) }} ...<a
                                    href="javascript:void(0);"
                                    @click="showNote(item)"
                                >{{ __('orders.more') }}</a>
                            </span>
                            <span v-else>
                                {{ item.note }}
                            </span>
                        </td>
                        <td v-else />
                    </template>
                    <template #actions="{item}">
                        <td>
                            <div
                                v-if="userPermissions.includes('task.manage')"
                                style="display: flex; grid-gap: 10px"
                            >
                                <DButton
                                    v-if="taskOpen(item)"
                                    mini
                                    icon="check"
                                    color="success"
                                    :disabled="isInLoadingState"
                                    @click="markTaskAsCompleted(item.id)"
                                />
                                <DButton
                                    v-else
                                    mini
                                    icon="undo-variant"
                                    color="danger"
                                    :disabled="isInLoadingState"
                                    @click="reOpenTask(item.id)"
                                />
                                <DButton
                                    mini
                                    :label="__('tasks.table.note.label')"
                                    icon="pencil"
                                    :disabled="isInLoadingState"
                                    @click="showTaskNoteDialog(item)"
                                />
                            </div>
                        </td>
                    </template>
                </DTable>
                <DPagination
                    v-if="tasks.last_page > 1"
                    :active-page="tasks.current_page"
                    :pages="tasks.last_page"
                    align="center"
                    @update:activePage="changePage"
                />
                <DModal
                    v-if="saveNote == true"
                    :show="showNoteModal"
                    :title="noteModalTitle"
                    is-big
                    is-confirm
                    @close="showNoteModal = false"
                    @confirm="saveTaskNote"
                >
                    <div class="p-3">
                        <TaskNoteForm
                            v-model:note="selectedItem.note"
                            :errors="errors"
                            :disabled="false"
                            @submit="saveTaskNote"
                        />
                    </div>
                </DModal>
                <DModal
                    v-else
                    :show="showNoteModal"
                    :title="noteModalTitle"
                    is-big
                    @close="showNoteModal = false"
                >
                    <div class="p-3">
                        <TaskNoteForm
                            v-model:note="selectedItem.note"
                            :errors="errors"
                            :disabled="true"
                        />
                    </div>
                </DModal>
            </DCol>
        </DRow>
    </DCard>
</template>

<script>
import TaskNoteForm from '~@/Components/Tasks/Notes/Form'
import _ from 'lodash'

const subjectMapping = {
    customer: {
        permission: 'customer.view',
        route: 'customers.customers.show',
        parameters: {
            customer: 'customer_id'
        }
    },
    contract: {
        permission: 'contract.view',
        route: 'customers.customers.contracts.index',
        parameters: {
            customer: 'customer_id',
            contract: 'subject_id'
        }
    },
    invoice: {
        permission: 'invoice.view',
        route: 'customers.customers.invoices.show',
        parameters: {
            customer: 'customer_id',
            invoice: 'subject_id'
        }
    },
    credit_note: {
        permission: 'credit_note.manage',
        route: 'customers.customers.credit-notes.show',
        parameters: {
            customer: 'customer_id',
            credit_note: 'subject_id'
        }
    },
    product: {
        permission: 'product.view',
        route: 'products.products.show',
        parameters: {
            product: 'subject_id'
        }
    },
    reminder: {
        permission: 'dunning.manage',
        route: 'tools.dunning.index',
        parameters: {}
    },
    termination: {
        permission: 'contract.manage_terminations',
        route: 'contracts.terminations',
        parameters: {}
    },
    reseller_contract: {
        permission: 'contract.view',
        route: 'customers.customers.contracts.reseller_contracts',
        parameters: {
            customer: 'customer_id',
            contract: 'subject_id'
        }
    },
    synced_contract: {
        permission: 'contract.view',
        route: 'customers.customers.contracts.reseller_contracts',
        parameters: {
            customer: 'customer_id',
            contract: 'additional_data.parent_contract_id',
            synced_contract: 'subject_id'
        }
    },
    incident: {
        permission: 'customer.incidents_view',
        route: 'customers.customers.business-incidents.edit',
        parameters: {
            customer: 'customer_id',
            business_incident: 'subject_id'
        }
    },
    partner_api_products: {
        permission: 'products.partner_api',
        route: 'products.partner-api-products.edit',
        parameters: {
            contract: 'subject_id',
        }
    },
    sepa_xml: {
        permission: 'payment.sepa',
        route: 'tools.sepa.transactions',
        parameters: {
            search: 'subject_id'
        }
    }
}

export default {
    name: 'TasksTable',
    components: {
        TaskNoteForm
    },
    props: {
        tasks: {
            type: Object,
            default: () => { return {} }
        },
        statuses: {
            type: Array,
            default: () => { return [] }
        },
        routeName: {
            type: String,
            default: ''
        },
        typeWithCount: {
            type: Array,
            default: () => { return [] }
        },
        brands: {
            type: Array,
            default: () => { return [] }
        },
        customerSegments: {
            type: Array,
            default: () => { return [] }
        },
        filterData: {
            type: Object,
            default: () => { return {} }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        groupTypes: {
            type: Array,
            default: () => { return [] }
        },
        taskList: {
            type: Array,
            default: () => { return [] }
        },
        markedAsCompleted: {
            type: Array,
            default: () => { return [] }
        },
        markedAsReopened: {
            type: Array,
            default: () => { return [] }
        }
    },
    emits: ['mark-task-as-completed', 're-open-task'],
    data () {
        return {
            fields: [
                { key: 'created_at', label: this.__('tasks.table.date'), filter: false },
                { key: 'message', label: this.__('tasks.table.message'), filter: false },
                { key: 'type', label: this.__('tasks.table.type') },
                { key: 'status', label: this.__('tasks.table.status') },
                { key: 'brand', label: this.__('tasks.table.brand') },
                { key: 'customer', label: this.__('tasks.table.customer'), filter: false },
                { key: 'subject', label: this.__('tasks.table.subject'), filter: false },
                { key: 'note', label: this.__('tasks.table.note.label'), filter: false },
                { key: 'updated_at', label: this.__('tasks.table.updated'), filter: false },
                { key: 'actions', label: '', sorter: false, filter: false }
            ],
            subjectMapping,
            filter: {
                statuses: (this.filterData !== null && this.filterData.statuses) || this.getParamArray('statuses'),
                types: (this.filterData !== null && this.filterData.types) || this.getParamArray('types'),
                search: (this.filterData !== null && this.filterData.search) || this.getParam('search'),
                brands: (this.filterData !== null && this.filterData.brands) || this.getParamArray('brands'),
                customer_segments: (this.filterData !== null && this.filterData.customer_segments) || this.getParamArray('customer_segments')
            },
            selectedItem: {},
            showNoteModal: false,
            noteModalTitle: '',
            saveNote: false
        }
    },
    computed: {
        userPermissions () {
            return this.$page.props.auth.permissions
        },
        customerSegmentsOptions () {
            return this.customerSegments.map((item) => {
                return {
                    value: item.value,
                    text: item.text,
                    selected: this.filterData?.customer_segments?.includes(String(item.value))
                }
            })
        },
        statusOptions () {
            return this.statuses.map((value) => {
                return {
                    value,
                    text: this.__('tasks.table.status_' + value),
                    selected: this.filter.statuses.includes(value)
                }
            })
        },
        typeOptions () {
            return this.taskList.map((item) => {
                return {
                    value: item,
                    text: this.__('tasks.table.type_button.' + item),
                    selected: this.filter.types.includes(item)
                }
            })
        },
        brandOptions () {
            return this.brands.map((item) => {
                return {
                    value: String(item.value),
                    text: item.label,
                    selected: this.filter.brands.includes(String(item.value))
                }
            })
        }
    },
    methods: {
        customerName (customer) {
            if (customer.firstname === null && customer.lastname === null) {
                return customer.company
            }

            return `${customer.firstname} ${customer.lastname}`
        },
        customerUrl (customerId) {
            return this.$route('customers.customers.show', {
                customer: customerId
            })
        },
        subjectLinkable (item) {
            return item.subject_type && this.subjectMapping[item.subject_type] !== 'undefined' &&
                this.userPermissions.includes(this.subjectMapping[item.subject_type].permission)
        },
        subjectLink (item) {
            const subjectMappingElement = this.subjectMapping[item.subject_type]
            const routeParameters = {}

            for (const [key, value] of Object.entries(subjectMappingElement.parameters)) {
                const resolvedValue = value
                    .split('.')
                    .reduce((obj, prop) => obj?.[prop], item)
                routeParameters[key] = resolvedValue ?? 0
            }

            return this.$route(subjectMappingElement.route, routeParameters)
        },
        subjectText (item) {
            if (item.subject_type) {
                return `${item.subject_type.replace('_', ' ')} (${item.subject_id})`
            }

            return item.subject_id
        },
        changePage (page) {
            const query = this.filter

            if (page > 1) {
                query.page = page
            } else {
                query.reset = 0
            }

            this.$inertia.get(this.$route(this.routeName, {
                save_filters: true
            }), query)
        },
        taskOpen (task) {
            return this.taskStatus(task) === 'open'
        },
        taskStatus (task) {
            if (this.markedAsCompleted.includes(task.id)) {
                return 'done'
            } else if (this.markedAsReopened.includes(task.id)) {
                return 'open'
            }

            return task.status
        },
        isBatch (task) {
            return this.markedAsCompleted.includes(task.id) || this.markedAsReopened.includes(task.id)
        },
        markTaskAsCompleted (taskId) {
            this.$emit('mark-task-as-completed', taskId)
        },
        reOpenTask (taskId) {
            this.$emit('re-open-task', taskId)
        },
        getParamArray (paramName) {
            const params = new URLSearchParams(window.location.search)
            const paramArray = []
            const expression = new RegExp('^' + paramName + '[\\[]\\d*[\\]]$')
            for (const param of params.entries()) {
                if (param[0].match(expression)) {
                    paramArray.push(param[1])
                }
            }
            return paramArray
        },
        getParam (paramName) {
            const params = new URLSearchParams(window.location.search)
            return params.has(paramName) ? params.get(paramName) : null
        },
        onFilterUpdate (type, value, doNotRefresh) {
            if (doNotRefresh) {
                this.filter[type] = value || null

                return
            }

            const filter = _.cloneDeep(this.filter)
            filter[type] = value || null

            this.$inertia.get(this.$route('tasks.tasks.index', {
                _query: filter,
                save_filters: true
            }))
        },
        filterTasksBy (type) {
            this.filter.types = []
            type.forEach(el => {
                this.filter.types.push(el)
            })
            this.filter.statuses = ['open']
            this.$inertia.get(this.$route('tasks.tasks.index', {
                _query: this.filter,
                save_filters: true
            }))
        },
        resetFilters () {
            this.$inertia.get(this.$route('tasks.tasks.index', {
                reset: 1, default_filters: true, save_filters: true
            }))
        },
        showNote (task) {
            this.selectedItem = task
            this.noteModalTitle = this.__('tasks.table.note.show_title')
            this.saveNote = false
            this.showNoteModal = true
        },
        showTaskNoteDialog (task) {
            this.selectedItem = _.cloneDeep(task)
            this.noteModalTitle = this.__('tasks.table.note.create_title')
            this.saveNote = true
            this.showNoteModal = true
        },
        saveTaskNote () {
            this.$inertia.post(
                this.$route('tasks.tasks.save-note', {
                    task: this.selectedItem.id
                }),
                { note: this.selectedItem.note }
            )
        },
        groupItemTypeCount (groupItem) {
            let count = 0
            groupItem.types.forEach(groupItemType => {
                this.typeWithCount.forEach(item => {
                    if (groupItemType === item.type) {
                        count += item.count
                    }
                })
            })
            return count
        }
    }
}
</script>
